import React, { useMemo, useEffect, useState } from "react"
import moment from "moment"
import {
  Autocomplete,
  Box,
  Chip,
  Grid,
  Switch,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { StyledSectionCard } from "shared/styled/General"
import { RecertificationSectionProps } from "../interface/examinationSettings"
import { ReminderContext, ReminderTiming } from "../constants/examinationSettings"
import { useTranslation } from "react-i18next"

const toBackendMidnight = (m: moment.Moment) => m.startOf("day").format("YYYY-MM-DDT00:00:00")

export function RecertificationSection({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  submitCount
}: RecertificationSectionProps) {
  const today = useMemo(() => moment().startOf("day"), [])
  const [showTurnOffSwitchModal, setShowTurnOffSwitchModal] = useState(false)
  const { t } = useTranslation()

  const recert = values.recertification ?? {
    id: null,
    customerExaminationId: null,
    isActive: false,
    startDate: null,
    endDate: null
  }

  const recertReminderDays: number[] = useMemo(() => {
    const reminders = Array.isArray(values.reminders) ? values.reminders : []
    return reminders
      .filter((r) => r.context === ReminderContext.Recertification && r.timing === ReminderTiming.BeforeEnd)
      .map((r) => r.daysOffset)
      .sort((a, b) => b - a)
  }, [values.reminders])

  const options = useMemo(() => {
    if (!recert.isActive || !recert.endDate) return []

    const end = moment(recert.endDate).endOf("day")
    const start = moment(recert.startDate ?? today).startOf("day")
    const diffInDays = end.diff(start, "days")

    if (diffInDays < 1) return []
    return Array.from({ length: diffInDays }, (_, i) => i + 1)
  }, [recert.isActive, recert.endDate, recert.startDate, today])

  // Keep reminder offsets valid if the date range shrinks
  useEffect(() => {
    if (!recert.isActive) return

    const max = options.length
    const normalized = Array.from(new Set(recertReminderDays.filter((n) => n >= 1 && n <= max))).sort(
      (a, b) => b - a
    )

    if (normalized.join(",") === recertReminderDays.join(",")) return

    const existing = Array.isArray(values.reminders) ? values.reminders : []
    const keep = existing.filter(
      (r) => !(r.context === ReminderContext.Recertification && r.timing === ReminderTiming.BeforeEnd)
    )

    const rebuilt = normalized.map((daysOffset) => ({
      context: ReminderContext.Recertification,
      timing: ReminderTiming.BeforeEnd,
      daysOffset
    }))

    setFieldValue("reminders", [...keep, ...rebuilt])
  }, [options.length, recert.isActive])

  const confirmTurnOffRecertification = () => {
    setShowTurnOffSwitchModal(false)
    setFieldValue("recertification.isActive", false)
    setFieldValue("recertification.startDate", null)
    setFieldValue("recertification.endDate", null)

    const existing = Array.isArray(values.reminders) ? values.reminders : []
    const keep = existing.filter(
      (r) => !(r.context === ReminderContext.Recertification && r.timing === ReminderTiming.BeforeEnd)
    )
    setFieldValue("reminders", keep)
  }

  const cancelTurnOffRecertification = () => {
    setShowTurnOffSwitchModal(false)
  }

  return (
    <StyledSectionCard>
      <Dialog open={showTurnOffSwitchModal} onClose={cancelTurnOffRecertification}>
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Typography variant="h2">
            {t("learning.admin.settings.turn_off_recertification", "Turn off the recertification period?")}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pb: 4 }}>
          <DialogContentText>
            <Typography color="textSecondary">
              {t(
                "learning.admin.settings.confirm_turn_off_recert_body",
                "If you turn off the recertification period, all approvals made during the period will be reset. This means that you will no longer be able to see which users have recertified during the period."
              )}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, display: "flex", justifyContent: "space-between" }}>
          <Button onClick={cancelTurnOffRecertification} variant="outlined">
            {t("global.cancel", "Cancel")}
          </Button>
          <Button onClick={confirmTurnOffRecertification} variant="contained" color="primary">
            {t("global.ok", "OK")}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid item md={12} display="flex" flexDirection="column" gap={2}>
        <Grid container spacing={3} alignItems="center" display="flex" justifyContent="space-between">
          <Grid item md={10} lg={6}>
            <Typography mb={1} fontWeight={600} variant="bodyXlarge">
              {t("learning.admin.settings.recertification_period", "Recertification period")}
            </Typography>
            <Typography color="textSecondary" variant="bodyMedium">
              {t(
                "learning.admin.settings.send_start_email_info",
                "Send start email at the start of the recertification period"
              )}
            </Typography>
          </Grid>

          <Grid item md={2} lg={4}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Box display="flex" alignItems="center">
                <Switch
                  id="recertification.isActive"
                  checked={!!recert.isActive}
                  onChange={(e) => {
                    const checked = e.target.checked

                    if (checked) {
                      setFieldValue("recertification.isActive", true)
                      if (!recert.startDate) {
                        setFieldValue("recertification.startDate", toBackendMidnight(today))
                      }
                      return
                    }

                    const hasRecertData = !!recert.startDate || !!recert.endDate

                    if (hasRecertData) {
                      setShowTurnOffSwitchModal(true)
                      return
                    }

                    setFieldValue("recertification.isActive", false)
                  }}
                />
                <Typography variant="bodyMedium">{t("learning.admin.settings.button.on", "On")}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {recert.isActive && (
          <>
            <Grid container spacing={3} alignItems="center" display="flex" justifyContent="space-between">
              <Grid item md={6}>
                <Typography mb={1} variant="bodyLarge" fontWeight="600">
                  {t("learning.admin.settings.start_end_date", "Start and end date")}
                </Typography>
                <Typography color="textSecondary" variant="bodyMedium">
                  {t(
                    "learning.admin.settings.start_end_date_info",
                    "Enter the start and end dates for the recertification period."
                  )}
                </Typography>
              </Grid>

              <Grid item md={3} xs={12}>
                <Box display="flex" flexDirection="column">
                  <Typography mb={1} variant="bodyMedium">
                    {t("learning.admin.settings.start_date", "Start date")}
                  </Typography>

                  <DatePicker
                    disabled={!recert.isActive}
                    minDate={today}
                    value={recert.startDate ? moment(recert.startDate) : null}
                    format="YYYY-MM-DD"
                    onChange={(value) => {
                      setFieldTouched("recertification.startDate", true, false)
                      const m = moment(value)
                      setFieldValue("recertification.startDate", m.isValid() ? toBackendMidnight(m) : null)
                    }}
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        helperText:
                          touched?.recertification?.startDate && errors?.recertification?.startDate
                            ? errors.recertification.startDate
                            : (touched?.recertification?.endDate || submitCount > 0) &&
                                errors?.recertification?.endDate
                              ? " "
                              : null,
                        error: Boolean(
                          touched?.recertification?.startDate && errors?.recertification?.startDate
                        ),
                        sx: { width: "100%" }
                      }
                    }}
                  />
                </Box>
              </Grid>

              <Grid item md={3} xs={12}>
                <Box display="flex" flexDirection="column">
                  <Typography mb={1} variant="bodyMedium">
                    {t("learning.admin.settings.end_date", "End date")}
                  </Typography>

                  <DatePicker
                    disabled={!recert.isActive}
                    minDate={recert.startDate ? moment(recert.startDate) : today}
                    value={recert.endDate ? moment(recert.endDate) : null}
                    format="YYYY-MM-DD"
                    onChange={(value) => {
                      setFieldTouched("recertification.endDate", true, false)
                      const m = moment(value)
                      setFieldValue("recertification.endDate", m.isValid() ? toBackendMidnight(m) : null)
                    }}
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        helperText:
                          (touched?.recertification?.endDate || submitCount > 0) &&
                          errors?.recertification?.endDate
                            ? errors.recertification.endDate
                            : null,
                        error: Boolean(
                          (touched?.recertification?.endDate || submitCount > 0) &&
                          errors?.recertification?.endDate
                        ),
                        sx: { width: "100%" }
                      }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={3} alignItems="center" display="flex" justifyContent="space-between">
              <Grid item md={6}>
                <Typography mb={1} variant="bodyLarge" fontWeight="600">
                  {t("learning.admin.settings.recertification_reminders", "Recertification reminders")}
                </Typography>
                <Typography color="textSecondary" variant="bodyMedium">
                  {t(
                    "learning.admin.settings.recertification_reminders_info",
                    "Choose how many days before the end date to send additional notifications."
                  )}
                </Typography>
              </Grid>

              <Grid item md={6} xs={12}>
                <Typography mb={1} variant="bodyMedium">
                  {t("learning.admin.settings.choose_one_or_more_values", "Choose one or more values")}
                </Typography>

                <Autocomplete
                  fullWidth
                  multiple
                  disabled={!recert.isActive || !recert.endDate || options.length === 0}
                  value={recertReminderDays}
                  options={options}
                  onChange={(_, newValue) => {
                    setFieldTouched("reminders", true, false)

                    const v = Array.from(new Set(newValue)).sort((a, b) => b - a)

                    const existing = Array.isArray(values.reminders) ? values.reminders : []
                    const keep = existing.filter(
                      (r) =>
                        !(
                          r.context === ReminderContext.Recertification &&
                          r.timing === ReminderTiming.BeforeEnd
                        )
                    )

                    const rebuilt = v.map((daysOffset) => ({
                      context: ReminderContext.Recertification,
                      timing: ReminderTiming.BeforeEnd,
                      daysOffset
                    }))

                    setFieldValue("reminders", [...keep, ...rebuilt])
                  }}
                  getOptionLabel={(opt) => `${opt} ${opt === 1 ? t("global.day", "day")?.toLowerCase() : t("global.days", "days")}`}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue
                      .slice()
                      .sort((a, b) => b - a)
                      .map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={`${option} ${option === 1 ? t("global.day", "day")?.toLowerCase() : t("global.days", "days")}`}
                          variant="outlined"
                        />
                      ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      helperText={touched?.reminders && errors?.reminders ? errors.reminders : null}
                      error={Boolean(touched?.reminders && errors?.reminders)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </StyledSectionCard>
  )
}
